// AssignPlayersToAgentModal — Phase 16.6 admin-only modal.
//
// Lets the admin attach EXISTING players to an agent without leaving
// the agent's detail page. Multi-select picker with a search box. The
// reverse direction of ReassignModal: this one is anchored to a fixed
// target agent and lets admin pick N players to move TO them.
//
// Flow:
//   1. Open with `agentId` and `agentLabel` props (the target).
//   2. Loads /admin/users via the existing list endpoint, paged.
//   3. Admin types in search → debounced refetch.
//   4. Admin checks N players. Already-on-this-agent players are
//      hidden from the picker (no-op work).
//   5. Confirm fires N parallel POSTs to
//        /admin/users/:id/reassign-agent  { new_agent_id: agentId }
//      Reports success count + any failures.
//
// Backend: each POST is gated by ensureAdmin (admin role only).
// The modal is mounted only when the caller is admin, but the
// backend is the real enforcement boundary.

import { useEffect, useMemo, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { INPUT_CLS } from './fieldStyles';

type UserRow = {
  id: number;
  username: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string | null;
  phone: string | null;
  agent_id: number | null;
  agent_username: string | null;
  status: string;
  wallet_balance: number;
};

type ListResp = { rows: UserRow[]; total: number; limit: number; offset: number };

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

export default function AssignPlayersToAgentModal({
  agentId, agentLabel, onClose, onDone,
}: {
  agentId: number;
  agentLabel: string;
  onClose: () => void;
  onDone: (assignedCount: number) => void;
}) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [data, setData] = useState<ListResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; failed: number } | null>(null);

  // 250ms search debounce
  useEffect(() => {
    const h = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(h);
  }, [search]);

  // Fetch on debounced-search change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const qs = new URLSearchParams({ limit: '50' });
        if (debounced) qs.set('search', debounced);
        const r = await adminApi.get<ListResp>(`/admin/users?${qs.toString()}`);
        if (!cancelled) setData(r);
      } catch (e) { if (!cancelled) setErr((e as ApiError).message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [debounced]);

  // Available = players whose current agent is NOT this agent
  const available = useMemo(() =>
    (data?.rows || []).filter((r) => r.agent_id !== agentId),
  [data, agentId]);

  // Already on this agent = hidden but counted (for the "already assigned" hint)
  const alreadyOnThisAgent = useMemo(() =>
    (data?.rows || []).filter((r) => r.agent_id === agentId).length,
  [data, agentId]);

  const togglePick = (id: number) => {
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (picked.size === 0) return;
    if (!confirm(`Assign ${picked.size} player${picked.size === 1 ? '' : 's'} to @${agentLabel}? Players currently under another agent will be moved.`)) return;
    setBusy(true); setErr(null); setProgress({ done: 0, total: picked.size, failed: 0 });
    let done = 0, failed = 0;
    for (const userId of Array.from(picked)) {
      try {
        await adminApi.post(`/admin/users/${userId}/reassign-agent`, {
          new_agent_id: agentId,
          reason: `Bulk assign from agent detail page`,
        });
      } catch (e) {
        failed++;
        // Don't bail on a single failure — report at the end.
        // eslint-disable-next-line no-console
        console.error(`Failed to assign user ${userId}:`, (e as ApiError).message);
      } finally {
        done++;
        setProgress({ done, total: picked.size, failed });
      }
    }
    setBusy(false);
    if (failed === 0) {
      onDone(done);
    } else {
      setErr(`${done - failed} of ${done} assigned. ${failed} failed (see console for details).`);
      // Still trigger the parent refresh so the partial result lands.
      onDone(done - failed);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-start sm:place-items-center p-3 sm:p-4 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-arena-800 rounded-xl ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 shadow-2xl w-full max-w-2xl flex flex-col my-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b ring-arena-200/60 dark:ring-arena-600/40">
          <div>
            <div className="text-base font-bold text-neutral-900 dark:text-white">
              Assign existing players
            </div>
            <div className="text-[11px] text-arena-600 dark:text-neon-200/60">to @{agentLabel}</div>
          </div>
          <button onClick={onClose} disabled={busy}
                  className="text-2xl leading-none text-arena-400 dark:text-neon-200/70 hover:text-arena-700 dark:hover:text-white disabled:opacity-50">×</button>
        </div>

        {/* Search */}
        <div className="px-5 pt-3 pb-2 space-y-2">
          <input type="text"
                 placeholder="Search by username, email, or phone…"
                 value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 className={INPUT_CLS} />
          <div className="text-[11px] text-arena-600 dark:text-neon-200/60 flex items-center justify-between">
            <span>
              {loading ? 'Searching…' : data ? `${available.length} available` : 'Loading…'}
              {alreadyOnThisAgent > 0 && ` · ${alreadyOnThisAgent} already on this agent (hidden)`}
            </span>
            <span>
              {picked.size > 0 && `${picked.size} selected`}
            </span>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-2 pb-2 min-h-[200px]">
          {err && (
            <div className="mx-3 px-3 py-2 rounded bg-rose-50 dark:bg-rose-500/10 text-red-700 text-xs">
              {err}
            </div>
          )}
          {!loading && available.length === 0 && !err && (
            <div className="px-4 py-10 text-center text-sm text-arena-600 dark:text-neon-200/60">
              {search.trim() ? 'No matching players.' : 'No players to assign.'}
            </div>
          )}
          <div className="divide-y divide-arena-200/40 dark:divide-arena-700/40">
            {available.map((u) => {
              const isPicked = picked.has(u.id);
              return (
                <button key={u.id} type="button"
                  onClick={() => togglePick(u.id)} disabled={busy}
                  className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition
                              ${isPicked ? 'bg-emerald-50 dark:bg-emerald-500/10' : 'hover:bg-arena-50 dark:hover:bg-arena-700/40'}`}>
                  <input type="checkbox" readOnly checked={isPicked}
                         className="w-4 h-4 accent-emerald-600 pointer-events-none" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-bold text-neutral-900 dark:text-white truncate">{(((u.first_name || "") + " " + (u.last_name || "")).trim() || "—")}</span>
                      <span className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60">#{u.id}</span>
                    </div>
                    <div className="text-[11px] text-arena-600 dark:text-neon-200/60 truncate">
                      {u.phone || u.email || '—'}
                      {u.agent_id != null && (
                        <span className="ml-2 text-amber-700 dark:text-amber-300">
                          · currently under @{u.agent_username || `agent-${u.agent_id}`}
                        </span>
                      )}
                      {u.agent_id == null && (
                        <span className="ml-2 text-cyan-700 dark:text-cyan-300">· unassigned</span>
                      )}
                    </div>
                  </div>
                  <div className="text-[11px] tabular-nums text-emerald-700 dark:text-emerald-300 font-bold shrink-0">
                    {inr(u.wallet_balance)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t ring-arena-200/60 dark:ring-arena-600/40 flex items-center justify-between gap-3">
          <div className="text-[11px] text-arena-600 dark:text-neon-200/60">
            {progress
              ? `${progress.done}/${progress.total} processed${progress.failed ? ` · ${progress.failed} failed` : ''}`
              : data && data.total > data.rows.length
              ? `Showing ${data.rows.length} of ${data.total}. Refine search to find more.`
              : ''}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={busy}
                    className="px-3 py-2 text-xs uppercase tracking-wider hover:underline disabled:opacity-50">
              Cancel
            </button>
            <button onClick={submit} disabled={busy || picked.size === 0}
                    className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
              {busy && <Icon name="hourglass_empty" size={14} className="animate-spin" />}
              {busy ? 'Assigning…' : `Assign ${picked.size || ''} player${picked.size === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
